// Phase 9 Plan 06 Task 1 — Top-level constraint evaluator dispatcher.
//
// Single public entry point `evaluate(constraints, context): EvaluationResult`
// orchestrating the Wave 1 (Plan 09-03) primitives:
//   - detectCycles   → cycle participants
//   - resolveConflicts → first-wins winners/losers
//   - walkPredicate  → selectability predicate evaluation
// And dispatching to 5 per-kind evaluators via an exhaustive switch.
//
// CANONICAL ORDER (per RESEARCH §evaluator.ts lines 532-538):
//   1. Cycle detection — cyclic constraints get `status: "cycle"` verdicts
//      (they do NOT short-circuit; non-cyclic constraints still evaluate).
//   2. Conflict resolution on non-cyclic constraints — losers get
//      `status: "superseded"` verdicts with the winning constraint id.
//   3. Per-constraint dispatch via evaluateOne() against EvalContext.
//
// PURE: no React, no IO, no DOM. Safe to call at author UI validation time
// AND at SpreadComposer render time.
//
// EXHAUSTIVENESS: evaluateOne's default branch carries the canonical
// `const _exhaustive: never = constraint` compile-time check — adding a 6th
// constraint kind without updating this dispatcher fails the typecheck.
// Runtime fallback throws (defense in depth, T-09-06-02 mitigation).
//
// pathInState v1 LIMITATION (Q-04 + Q-09 plan-phase resolutions):
//   The Constraint AST treats pathInState as dot-notation strings (e.g.
//   "selected" or "batches[0].multiplier" per constraint.ts lines 5-6 comment).
//   Wave 2 supports ONLY single-segment top-level keys (no dots, no brackets);
//   compound paths fall back to undefined. Complex path syntax is deferred to
//   a Phase 15 polish pass once author UI evolves the path picker (Q-09).
//
// Q-02 stable ids: `constraint-${index}` matches Wave 1 cycle-detection.ts and
// conflict.ts — the renderer always looks up by document position.
//
// Q-05 AND-compose: selectability constraints never appear in supersededIds
// because conflict.ts returns null from conflictKey for them; this dispatcher
// inherits that behavior — every selectability constraint evaluates and the
// renderer surfaces all violations (not first-wins).
//
// PARANOID-STRICT compliance (Phase 01.1 D-15):
//   isolatedDeclarations:        every export has an explicit annotation.
//   verbatimModuleSyntax:        `import type` for type-only imports.
//   exactOptionalPropertyTypes:  honored in ConstraintVerdict union members.
//   noUncheckedIndexedAccess:    defensive narrows on Map.get + Record reads.
//   noFallthroughCasesInSwitch:  every switch arm returns explicitly.

import type { Constraint } from "../schemas/constraint.js";
import { detectCycles } from "./cycle-detection.js";
import { resolveConflicts } from "./conflict.js";
import { walkPredicate } from "./predicate.js";
import type { ConstraintVerdict, EvalContext, EvaluationResult } from "./types.js";

// ---------------------------------------------------------------------------
// Public API — the only export from this module.
// ---------------------------------------------------------------------------

/**
 * Pure constraint evaluator. Runs in author UI (validation feedback) and the
 * SpreadComposer render path (option-disabled decisions + tooltip surfacing).
 *
 * D-01 typed dispatcher / D-02 cycles / D-03 first-wins / D-04 explanation
 * threading. CSTR-01/02/04/05 satisfied via the canonical 3-step orchestration.
 *
 * Order of operations:
 *   1. detectCycles(constraints) — union all participant ids into cycleParticipants.
 *   2. resolveConflicts(non-cyclic) — build supersededIds + winningIds sets.
 *   3. Iterate constraints in document order. For each:
 *      - id in cycleParticipants → push verdict { status:"cycle" }
 *      - id in supersededIds      → push verdict { status:"superseded", … }
 *      - else                     → call evaluateOne() against EvalContext.
 *
 * O(V + E + N) per evaluation. V = constraint count (≤ schema cap), E =
 * dependency edges (typically ≤ V), N = total predicate node count across
 * all selectability constraints (≤ 100 per Wave 1 walker bound).
 */
export function evaluate(
  constraints: ReadonlyArray<Constraint>,
  context: EvalContext,
): EvaluationResult {
  // Step 1: cycles — disqualify participants.
  const cycles = detectCycles(constraints);
  const cycleParticipants = new Set<string>();
  for (const c of cycles) {
    for (const id of c.participants) {
      cycleParticipants.add(id);
    }
  }

  // Step 2: conflicts (D-03 first-wins) on the non-cyclic pool.
  // Cycle-participating constraints are excluded from conflict resolution —
  // they are already disqualified by step 1.
  const conflictPool: Constraint[] = [];
  for (let i = 0; i < constraints.length; i++) {
    const c = constraints[i];
    if (c === undefined) continue;
    const id = getConstraintId(i);
    if (!cycleParticipants.has(id)) {
      conflictPool.push(c);
    }
  }
  // NOTE: resolveConflicts derives its own `constraint-${index}` ids from
  // conflictPool's positional index, not from the original `constraints` array.
  // We need to map the winning/superseded ids back to the original document
  // positions so the verdict loop below uses the same id space the caller
  // sees in `cycles[].chain`. We rebuild winning/superseded sets from the
  // ORIGINAL-position ids by walking the original array a second time.
  //
  // Implementation detail: resolveConflicts only consumes Constraint shapes
  // (kind + conflict-key fields), so we replicate first-wins resolution
  // inline here using the original positions, sidestepping the id-space
  // mismatch entirely. This is O(V) — identical cost to the call we'd have
  // made — and keeps the public id space consistent.
  const supersededConstraintIds = new Set<string>();
  const winningConstraintIds = new Set<string>();
  const supersededBy = new Map<string, string>(); // loser id → winner id

  // Local first-wins map keyed by conflict signature (mirrors conflict.ts
  // conflictKey, but in original-position id space).
  const seenByKey = new Map<string, { winnerId: string }>();
  for (let i = 0; i < constraints.length; i++) {
    const c = constraints[i];
    if (c === undefined) continue;
    const id = getConstraintId(i);
    if (cycleParticipants.has(id)) continue;
    const key = conflictKeyOf(c);
    if (key === null) continue; // selectability AND-composes — never conflicts.
    const existing = seenByKey.get(key);
    if (existing === undefined) {
      seenByKey.set(key, { winnerId: id });
    } else {
      supersededConstraintIds.add(id);
      supersededBy.set(id, existing.winnerId);
      winningConstraintIds.add(existing.winnerId);
    }
  }

  // The public `conflicts` array on EvaluationResult still uses the Wave 1
  // resolveConflicts shape — call it now on the non-cyclic pool so consumers
  // get a stable, well-typed ConstraintConflict[]. The pool's relative
  // positions are sufficient for surfacing "conflict between same-kind
  // constraints"; consumers that need original-position ids use the
  // winningConstraintIds + supersededConstraintIds sets above.
  const conflicts = resolveConflicts(conflictPool);

  // Step 3: per-constraint verdicts.
  const verdicts: ConstraintVerdict[] = [];
  const visibilityByWidgetId = new Map<string, boolean>();

  for (let i = 0; i < constraints.length; i++) {
    const constraint = constraints[i];
    if (constraint === undefined) continue;
    const id = getConstraintId(i);

    if (cycleParticipants.has(id)) {
      verdicts.push({ status: "cycle", constraintId: id, kind: constraint.kind });
      continue;
    }

    if (supersededConstraintIds.has(id)) {
      verdicts.push({
        status: "superseded",
        constraintId: id,
        kind: constraint.kind,
        supersededBy: supersededBy.get(id) ?? "",
        explanation: constraint.explanation,
      });
      continue;
    }

    verdicts.push(evaluateOne(constraint, context, id, visibilityByWidgetId));
  }

  return {
    verdicts,
    cycles,
    conflicts,
    winningConstraintIds,
    supersededConstraintIds,
    visibilityByWidgetId,
  };
}

// ---------------------------------------------------------------------------
// Internal — id helper.
// ---------------------------------------------------------------------------

function getConstraintId(index: number): string {
  return `constraint-${index}`;
}

// ---------------------------------------------------------------------------
// Internal — per-kind dispatcher with compile-time exhaustiveness check.
// ---------------------------------------------------------------------------

function evaluateOne(
  constraint: Constraint,
  context: EvalContext,
  id: string,
  visibilityOut: Map<string, boolean>,
): ConstraintVerdict {
  switch (constraint.kind) {
    case "cardinality":
      return evaluateCardinality(constraint, context, id);
    case "propagation":
      return evaluatePropagation(constraint, context, id);
    case "visibility":
      return evaluateVisibility(constraint, context, id, visibilityOut);
    case "dependency":
      return evaluateDependency(constraint, context, id);
    case "selectability":
      return evaluateSelectability(constraint, context, id);
    default: {
      // Compile-time exhaustiveness — adding a 6th constraint kind without
      // updating this dispatcher fails the typecheck. The throw is
      // defense-in-depth (T-09-06-02 mitigation) for any path that bypasses
      // the type system at runtime.
      const _exhaustive: never = constraint;
      void _exhaustive;
      throw new Error(
        `unknown constraint kind in evaluateOne: ${JSON.stringify(constraint)}`,
      );
    }
  }
}

// ---------------------------------------------------------------------------
// Per-kind evaluators (module-private).
// ---------------------------------------------------------------------------

function evaluateCardinality(
  constraint: Extract<Constraint, { kind: "cardinality" }>,
  context: EvalContext,
  id: string,
): ConstraintVerdict {
  const min = constraint.min ?? 0;
  const max = constraint.max;

  let count: number;
  switch (constraint.resource) {
    case "recipe-in-spread":
      count = context.spreadRecipeCount;
      break;
    case "option-in-widget": {
      const widgetState = context.widgetStates.get(constraint.scope);
      const selected = readPath(widgetState, "selected");
      count = Array.isArray(selected) ? selected.length : 0;
      break;
    }
    default: {
      const _exhaustive: never = constraint.resource;
      void _exhaustive;
      count = 0;
      break;
    }
  }

  const ok = count >= min && count <= max;
  return ok
    ? { status: "ok", constraintId: id, kind: "cardinality" }
    : {
        status: "violated",
        constraintId: id,
        kind: "cardinality",
        explanation: constraint.explanation,
      };
}

function evaluatePropagation(
  _constraint: Extract<Constraint, { kind: "propagation" }>,
  _context: EvalContext,
  id: string,
): ConstraintVerdict {
  // Propagation is a WRITE-side effect at user-action time. At eval-time it
  // is a pass-through: the constraint "fires" when trigger.widgetInstanceId
  // state changes; the SpreadComposer reducer applies the effect. The
  // evaluator emits "ok" so the constraint shows as ACTIVE in the author UI
  // (the actual propagation lives in the SpreadComposer reducer).
  return { status: "ok", constraintId: id, kind: "propagation" };
}

function evaluateVisibility(
  constraint: Extract<Constraint, { kind: "visibility" }>,
  context: EvalContext,
  id: string,
  visibilityOut: Map<string, boolean>,
): ConstraintVerdict {
  const targetWidgetId = constraint.show.widgetInstanceId;
  const srcState = context.widgetStates.get(constraint.when.ref.widgetInstanceId);
  const srcValue = readPath(srcState, constraint.when.ref.pathInState);
  const isVisible = deepEqual(srcValue, constraint.when.equals);

  // First-write-wins. Superseded visibility constraints never reach this path
  // (step 2 of evaluate() routes them to the "superseded" verdict branch
  // before evaluateOne fires), so the guard is informational.
  if (!visibilityOut.has(targetWidgetId)) {
    visibilityOut.set(targetWidgetId, isVisible);
  }

  return isVisible
    ? { status: "ok", constraintId: id, kind: "visibility" }
    : {
        status: "violated",
        constraintId: id,
        kind: "visibility",
        explanation: constraint.explanation,
      };
}

function evaluateDependency(
  constraint: Extract<Constraint, { kind: "dependency" }>,
  context: EvalContext,
  id: string,
): ConstraintVerdict {
  const ifState = context.widgetStates.get(constraint.if.ref.widgetInstanceId);
  const ifValue = readPath(ifState, constraint.if.ref.pathInState);

  // Condition not active → dependency is dormant; verdict is ok.
  if (!deepEqual(ifValue, constraint.if.equals)) {
    return { status: "ok", constraintId: id, kind: "dependency" };
  }

  // Condition active → require path must be set (!== undefined && !== null).
  const reqState = context.widgetStates.get(constraint.require.widgetInstanceId);
  const reqValue = readPath(reqState, constraint.require.pathInState);
  const isSet = reqValue !== undefined && reqValue !== null;
  return isSet
    ? { status: "ok", constraintId: id, kind: "dependency" }
    : {
        status: "violated",
        constraintId: id,
        kind: "dependency",
        explanation: constraint.explanation,
      };
}

function evaluateSelectability(
  constraint: Extract<Constraint, { kind: "selectability" }>,
  context: EvalContext,
  id: string,
): ConstraintVerdict {
  const result = walkPredicate(constraint.predicate, context);
  if (!result.ok) {
    // Structural rejection (depth-exceeded / self-reference / unknown-variable)
    // — these shouldn't survive Phase 01.1 Zod validation, but the evaluator
    // surfaces them with the failure reason in the explanation for author-UI
    // debugging. Defense-in-depth path.
    return {
      status: "violated",
      constraintId: id,
      kind: "selectability",
      explanation: `${constraint.explanation} (predicate eval failed: ${result.reason} at ${result.nodePath})`,
    };
  }
  return result.value
    ? { status: "ok", constraintId: id, kind: "selectability" }
    : {
        status: "violated",
        constraintId: id,
        kind: "selectability",
        explanation: constraint.explanation,
      };
}

// ---------------------------------------------------------------------------
// Internal — conflict-key replication.
//
// Mirrors conflict.ts conflictKey() so we can resolve first-wins in the
// original-position id space without re-implementing the Wave 1 module.
// Any future change to the conflict signature in conflict.ts must be
// reflected here too — keep them lockstep.
// ---------------------------------------------------------------------------

function conflictKeyOf(c: Constraint): string | null {
  switch (c.kind) {
    case "visibility":
      return `visibility::${c.show.widgetInstanceId}::${c.show.pathInState}`;
    case "cardinality":
      return `cardinality::${c.resource}::${c.scope}`;
    case "dependency":
      return `dependency::${c.if.ref.widgetInstanceId}::${c.if.ref.pathInState}::${JSON.stringify(
        c.if.equals,
      )}`;
    case "propagation":
      return `propagation::${c.trigger.widgetInstanceId}::${c.trigger.pathInState}`;
    case "selectability":
      // Q-05 — selectability AND-composes (all must permit). The dispatcher
      // evaluates each independently; never participates in first-wins.
      return null;
    default: {
      const _exhaustive: never = c;
      void _exhaustive;
      return null;
    }
  }
}

// ---------------------------------------------------------------------------
// Internal — pathInState reader.
//
// v1 LIMITATION (Q-04 + Q-09): only single-segment top-level keys are
// supported. Compound paths ("batches[0].multiplier") return undefined.
// Complex path syntax is a Phase 15 polish task — the author UI's
// visibility/dependency path picker will populate these as top-level keys
// in v1 (see RESEARCH §"v1 leaf grammar" — selectability leaf grammar
// carries no widgetInstanceId at all, which sidesteps the path question
// for that kind entirely).
// ---------------------------------------------------------------------------

function readPath(
  state: Readonly<Record<string, unknown>> | undefined,
  path: string,
): unknown {
  if (state === undefined) return undefined;
  // Compound paths (dots, brackets) fall back to undefined in v1.
  if (path.includes(".") || path.includes("[")) return undefined;
  // noPropertyAccessFromIndexSignature — use bracket access on Record.
  return state[path];
}

// ---------------------------------------------------------------------------
// Internal — deep equality.
//
// Pure deep-equal — no JSON.stringify (NaN-safe via Object.is, Date-safe
// because Date instances compare by referential identity here, which is
// what visibility/dependency constraints intend).
// ---------------------------------------------------------------------------

function deepEqual(a: unknown, b: unknown): boolean {
  if (Object.is(a, b)) return true;
  if (typeof a !== typeof b) return false;
  if (a === null || b === null) return false;
  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) {
      if (!deepEqual(a[i], b[i])) return false;
    }
    return true;
  }
  if (typeof a === "object" && typeof b === "object") {
    const ak = Object.keys(a as Record<string, unknown>);
    const bk = Object.keys(b as Record<string, unknown>);
    if (ak.length !== bk.length) return false;
    const aRec = a as Record<string, unknown>;
    const bRec = b as Record<string, unknown>;
    for (const k of ak) {
      if (!deepEqual(aRec[k], bRec[k])) return false;
    }
    return true;
  }
  return false;
}
